import React, { useEffect, useState } from 'react';
import { Gauge, Lock, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const LABELS = {
  location_retention_required: 'Standort-Aufbewahrung und ihre Freigabe fehlen',
  driver_location_shadow_required: 'Fahrer-GPS-v2 mit persistenter Shadow-Evidenz ist noch nicht bereit',
  fare_location_purpose_approval_required: 'Zweckfreigabe für streckenabhängige Abrechnung fehlt',
  android_driver_action_attestation_required: 'Android-Fahrer: Action-Attestierung muss auf Observe, Shadow oder Enforce stehen',
  ios_driver_action_attestation_required: 'iOS-Fahrer: Action-Attestierung muss auf Observe, Shadow oder Enforce stehen',
  android_driver_provider_readiness_required: 'Android-Fahrer: technische Provider-Konfiguration ist nicht bereit',
  android_driver_physical_e2e_required: 'Android-Fahrer: Provider-Nachweis fehlt',
  ios_driver_provider_readiness_required: 'iOS-Fahrer: technische Provider-Konfiguration ist nicht bereit',
  ios_driver_physical_e2e_required: 'iOS-Fahrer: Provider-Nachweis fehlt',
  driver_trace_cryptographic_chain_required: 'Die Fahrer-App bindet die einzelnen GPS-Punkte noch nicht kryptografisch',
  actual_distance_route_map_matching_required: 'Schleifen und nahe Parallelstrecken brauchen noch eine sequenzielle, mehrdeutigkeitssichere Routenzuordnung',
  actual_distance_quote_recipe_binding_required: 'Das exakte Engine-Rezept muss noch atomar aus demselben Preis-Lauf gebunden werden',
  active_ride_trace_retention_exemption_required: 'Aktive Fahrten brauchen noch einen gesicherten Schutz vor vorzeitiger Trace-Löschung',
  actual_distance_settlement_physical_e2e_required: 'Der komplette Ist-Strecken-Geldfluss wurde je Fahrer-Plattform noch nicht physisch E2E nachgewiesen',
};

export default function ActualDistanceSettlement() {
  const [status, setStatus] = useState({ loading: true });
  const [editing, setEditing] = useState(false);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    setStatus({ loading: true });
    return api.get('/admin/early-finish-distance-settlement')
      .then(({ data }) => setStatus(data && typeof data.configured_enabled === 'boolean'
        && Number.isInteger(data.revision) ? data : { unavailable: true }))
      .catch(() => setStatus({ unavailable: true }));
  };
  useEffect(() => { load(); }, []);

  const close = () => { setEditing(false); setPassword(''); setReason(''); setReference(''); };
  const save = async () => {
    const enabled = !status.configured_enabled;
    if (!password || reason.trim().length < 10 || (enabled && !status.purpose_approval_recorded && reference.trim().length < 3)) return;
    setBusy(true);
    try {
      await api.patch('/admin/early-finish-distance-settlement', {
        enabled,
        expected_revision: status.revision,
        confirm: enabled ? 'ENABLE-ACTUAL-DISTANCE-SETTLEMENT' : 'DISABLE-ACTUAL-DISTANCE-SETTLEMENT',
        password,
        reason: reason.trim(),
        ...(enabled && reference.trim() ? { purpose_approval_reference: reference.trim() } : {}),
      });
      toast.success(enabled ? 'Ist-Strecken-Abrechnung eingeschaltet.' : 'Ist-Strecken-Abrechnung ausgeschaltet.');
      close();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Einstellung konnte nicht gespeichert werden.');
    } finally { setBusy(false); }
  };

  const card = { background: '#fff', border: '1px solid #d1d5db', borderRadius: 14, padding: 24, maxWidth: 560 };
  if (status.loading) return <div style={card} role="status">Ist-Strecken-Abrechnung wird geprüft …</div>;
  if (status.unavailable) return (
    <div style={{ ...card, borderColor: '#f59e0b' }} role="alert">
      <b>Status der Ist-Strecken-Abrechnung nicht verfügbar</b>
      <p>Der Schalter bleibt aus Sicherheitsgründen gesperrt.</p>
      <button type="button" onClick={load}>Status erneut prüfen</button>
    </div>
  );

  const on = status.configured_enabled === true;
  return (
    <div style={{ ...card, borderColor: on ? '#15803d66' : '#d1d5db' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {on ? <ShieldCheck size={20} color="#15803d" /> : <Gauge size={20} color="#4b5563" />}
        <h2 style={{ margin: 0, fontSize: 16 }}>Streckenabhängige Abrechnung</h2>
      </div>
      <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6 }}>
        Nur nach dem Einschalten gebuchte Fahrten kommen für die Abrechnung infrage.
        Ab 80 % der angebotenen Strecke gilt der volle Transportpreis. Darunter wird mit den eingefrorenen
        Bedingungen der Fahrt der Transportpreis anteilig berechnet: Der Transportpreis bleibt mindestens beim eingefrorenen Mindestpreis;
        bereits gebundene Rabatte und Guthaben werden danach berücksichtigt. Der Transportpreis steigt nicht über den gebundenen Transportpreis.
        Nicht erreichte Zwischenstopps werden nicht berechnet. Zulässige Wartegebühren kommen nach bestehender Regel hinzu.
        Für ältere Angebote gilt ihre gespeicherte Regelversion.
        Fehlt für eine berechtigte Fahrt eine vertrauenswürdige Fahrer-Spur, bleibt der volle gebundene Preis bestehen.
        Der globale Zustand wird ausschließlich mit dem geschützten Owner-Schalter ein- oder ausgeschaltet.
      </p>
      <p style={{ fontSize: 13, fontWeight: 700 }}>
        Konfiguriert: {on ? 'AN' : 'AUS'}
      </p>
      <div style={{ fontSize: 13, lineHeight: 1.6 }}>
        <b>Voraussetzungen der Nachweiskette:</b>
        {['android', 'ios'].map((platform) => {
          const prerequisite = status.technical_prerequisites?.[platform];
          const readiness = prerequisite?.ready === true ? 'Bereit'
            : prerequisite?.ready === false ? 'Nicht bereit' : 'Unbekannt';
          return <div key={platform}>
            {platform === 'android' ? 'Android' : 'iOS'}: {readiness}
            {prerequisite?.reasons?.length > 0 && <ul>
              {prerequisite.reasons.map((key) => <li key={key}>{LABELS[key] || key}</li>)}
            </ul>}
          </div>;
        })}
        <p>
          Für diese Fahrt geeignet: Unbekannt — diese Übersicht enthält keine einzelne Fahrt.<br />
          Beim Abschluss angewandt: Unbekannt — entscheidet der Server für jeden Abschluss
          anhand des gebundenen Angebots, des verifizierten Abschlussnachweises und der Fahrer-Spur.
        </p>
        <p>Physischer Nachweis des gesamten Geldflusses: Unbekannt.
          Technische Bereitschaft bestätigt keinen vollständigen Gerätetest.</p>
      </div>
      {status.advisories?.length > 0 && (
        <div style={{ color: '#4b5563', fontSize: 12, lineHeight: 1.6 }}>
          <b>Technische Hinweise für den Versuch – kein Aktivierungsverbot:</b>
          <ul>
            {status.advisories.map((key) => <li key={key}>{LABELS[key] || key}</li>)}
          </ul>
        </div>
      )}
      {!editing ? (
        <button type="button" onClick={() => setEditing(true)}
          style={{ border: 0, borderRadius: 9, padding: '10px 15px', color: '#fff', background: on ? '#b91c1c' : '#111827', fontWeight: 700 }}>
          {on ? 'Ist-Strecken-Abrechnung ausschalten …'
            : 'Ist-Strecken-Abrechnung einschalten …'}
        </button>
      ) : (
        <div style={{ background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 12, padding: 16 }}>
          <p style={{ marginTop: 0, fontSize: 13, fontWeight: 700 }}><Lock size={13} /> Owner-Bestätigung</p>
          {!on && !status.purpose_approval_recorded && (
            <input aria-label="Zweckfreigabe-Referenz" value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder="Freigabe-/Dokument-Referenz" maxLength={200} />
          )}
          <input aria-label="Begründung" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Begründung (mindestens 10 Zeichen)" maxLength={300} />
          <input aria-label="Admin-Passwort" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin-Passwort" autoComplete="off" />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={close} disabled={busy}>Abbrechen</button>
            <button type="button" onClick={save} disabled={busy || !password || reason.trim().length < 10
              || (!on && !status.purpose_approval_recorded && reference.trim().length < 3)}>
              {busy ? 'Wird gespeichert …' : (on ? 'Ausschalten bestätigen' : 'Einschalten bestätigen')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
